/**
 * Canonical order-status taxonomy — the SINGLE source for these values.
 * `@stopal/db` builds the `order_status` Postgres enum FROM this array
 * (`pgEnum('order_status', ORDER_STATUSES)`), so the two can't drift and no
 * lockstep test is needed. This leaf package is the shared home because the
 * frontend can import it but can't import `@stopal/db`.
 *
 * Despite the enum's name, the column it types is `order_event.status` — one
 * fulfillment step, i.e. one trip. Nothing carries an `order`-level status.
 */
export const ORDER_STATUSES = [
  'pending',
  'confirmed',
  'scheduled',
  'in_progress',
  'completed',
  'cancelled',
] as const

export type OrderStatus = (typeof ORDER_STATUSES)[number]

/**
 * Whether this trip is over — nothing more will happen to it.
 *
 * An exhaustive switch rather than a list, because both sets below are read as
 * decisions about work that still has to happen, and a status added to
 * `order_status` later would otherwise be classified silently — as *finished*,
 * since it would simply be absent from a hand-listed unfinished set. Here it
 * fails to compile until somebody classifies it, and every reader of either set
 * inherits that.
 */
export function isFinishedOrderStatus(status: OrderStatus): boolean {
  switch (status) {
    case 'completed':
    case 'cancelled':
      return true
    case 'pending':
    case 'confirmed':
    case 'scheduled':
    case 'in_progress':
      return false
    default:
      return status satisfies never
  }
}

/**
 * Every status that is not finished — a trip that still owes physical work.
 *
 * Named for its derivation rather than "open", because it **includes
 * `pending`** and so is deliberately WIDER than
 * `OPEN_ORDER_EVENT_STATUSES` (`modules/order-events/pallet-actions.ts`),
 * which answers "is this trip committed and live?" and excludes an unpaid
 * prepared trip. Both filter the same column, so a reader who mistakes one for
 * the other gets no type error — only the names keep them apart.
 */
export const UNFINISHED_ORDER_STATUSES: ReadonlySet<OrderStatus> = new Set(
  ORDER_STATUSES.filter((status) => !isFinishedOrderStatus(status)),
)

/**
 * Its complement — a trip nothing more will happen to. `readonly` to match the
 * sibling above: a module singleton that a consumer could `push` to or empty is
 * one an accidental in-place build of a query array would corrupt for every
 * other reader, process-wide.
 */
export const FINISHED_ORDER_STATUSES: readonly OrderStatus[] =
  ORDER_STATUSES.filter(isFinishedOrderStatus)

/**
 * Whether each order status counts as a trip that is still OPEN — one the
 * customer has COMMITTED to, work not yet done and not cancelled.
 *
 * It lives here, beside `UNFINISHED_ORDER_STATUSES`, because the two are read
 * as alternatives and each docblock already tells you to read the other — and
 * because a leaf is the only home an authorization filter can import. It was
 * declared in `modules/order-events/pallet-actions.ts`, which reaches
 * `lib/owner-scope.ts` through `delivery-ordering.ts`; the site-access
 * predicate that now needs this set is IN `lib/owner-scope.ts`, so importing
 * it from there closed a cycle. That module re-exports these three names, so
 * every existing consumer still reads them from where it always did.
 *
 * A `Record` over the whole taxonomy rather than a list of the open ones,
 * because a list cannot demand completeness: a hand-typed tuple with
 * `satisfies readonly OrderStatus[]` pins the values and says nothing about
 * the ones missing. A status added to `order_status` is by construction
 * absent from it, so it is classified as *not open* — invisible to every
 * booked-trip guard, retrieval reconcile and stack floor in that module, with
 * nothing failing to compile. Here the record is incomplete until somebody
 * classifies it, and the compiler says so at this line.
 *
 * The obvious derivation — `ORDER_STATUSES.filter(pred)` — does not work,
 * because a bare filter widens the element type back to `OrderStatus`. Keying the union off this record instead
 * keeps the literals for free: see {@link OpenOrderEventStatus}.
 */
const ORDER_EVENT_OPENNESS = {
  // Prepared but unpaid. Deliberately NOT open — the customer has not agreed to
  // it — which is exactly the one status by which this set is narrower than
  // `UNFINISHED_ORDER_STATUSES` above. Both filter the same column and neither
  // is type-distinguishable from the other, so read both docblocks before
  // reaching for either.
  pending: 'closed',
  confirmed: 'open',
  scheduled: 'open',
  in_progress: 'open',
  completed: 'closed',
  cancelled: 'closed',
} as const satisfies Record<OrderStatus, 'open' | 'closed'>

/**
 * May a trip's paperwork be reprinted at a desk?
 *
 * Not the same question as openness, and reusing that set here is the mistake
 * to avoid: `completed` is `closed`, and a finished trip is the MOST ordinary
 * reprint — an archival copy of what was actually run. What must be refused is
 * a job nobody agreed to and a job nobody will run.
 *
 * - `pending` — prepared but unpaid. The customer has not agreed to it, so
 *   printing its label puts a sticker on a box for work that may never be
 *   bought, and writes a `label_print` row asserting one exists.
 * - `cancelled` — will never happen. The paper names a trip nobody will make.
 *
 * `currentTripForPallet` already refuses both on the by-barcode reprint door,
 * so this is the same ruling reaching the by-event door rather than a new one.
 *
 * Exhaustive over `OrderStatus`, so a new status decides here rather than
 * inheriting whichever answer the shape of the expression happened to give.
 */
const ORDER_EVENT_PAPERWORK = {
  pending: 'refuse',
  confirmed: 'reprintable',
  scheduled: 'reprintable',
  in_progress: 'reprintable',
  completed: 'reprintable',
  cancelled: 'refuse',
} as const satisfies Record<OrderStatus, 'reprintable' | 'refuse'>

/** Whether a label may be reprinted for a trip in this status. */
export function isReprintableOrderStatus(status: OrderStatus): boolean {
  return ORDER_EVENT_PAPERWORK[status] === 'reprintable'
}

/**
 * Does this trip belong on a PLACE's list of trips — the business site page's
 * "Trips to and from here"?
 *
 * A third question about the same column, and it is neither of the two above.
 * Openness excludes `completed` and `cancelled`, and a site's own history is
 * exactly what such a page is for; paperwork refuses `cancelled`, which this
 * keeps. Ruled (Marcus): an abandoned, unpaid checkout must
 * not be listed at a company's site — it tells them a truck is coming when
 * nothing was ever bought.
 *
 * - `pending` — prepared and never paid for. The whole subject of the ruling:
 *   hidden.
 * - `completed` and `cancelled` — KEPT. A place's page is partly its history,
 *   and both are things that happened to a trip there. Ruled with the rest.
 *
 * **What keeping `cancelled` costs, stated so nobody reads this set as
 * stronger than it is.** An abandoned checkout does not always STAY `pending`:
 * the abandon paths rewrite it to `cancelled`, which this set admits. The
 * column `order_event.cancelled_unpaid` tells it apart, and its docblock is
 * the account of who writes it. A reader of this set that must not show an
 * abandoned checkout owes that second condition, as the site's trip read does.
 *
 * This set differs from `OPEN_ORDER_EVENT_STATUSES` — which `palletConnectedTo`
 * resolves site ACCESS through — by the two history statuses and nothing else:
 * both exclude `pending`, so an unpaid prepared trip is neither listed at a
 * site nor a reason anybody may read it.
 *
 * Exhaustive over `OrderStatus` for the reason its two siblings are — a status
 * added later decides here rather than inheriting the shape of an expression.
 */
const ORDER_EVENT_PLACE_LISTING = {
  pending: 'hidden',
  confirmed: 'listed',
  scheduled: 'listed',
  in_progress: 'listed',
  completed: 'listed',
  cancelled: 'listed',
} as const satisfies Record<OrderStatus, 'listed' | 'hidden'>

/** Whether a trip in this status is listed among a place's own trips. */
export function isPlaceListedOrderStatus(status: OrderStatus): boolean {
  return ORDER_EVENT_PLACE_LISTING[status] === 'listed'
}

/**
 * The listable statuses, derived rather than listed so they cannot disagree
 * with the record above. The ORDER is `ORDER_STATUSES`' own; the one consumer
 * passes it to `inArray`.
 */
export const PLACE_LISTED_ORDER_STATUSES: readonly OrderStatus[] =
  ORDER_STATUSES.filter(isPlaceListedOrderStatus)

/**
 * The open statuses as a literal union, read OFF the record rather than typed
 * again beside it.
 *
 * **No reader is currently narrowed by this, measured** — widening it to
 * `OrderStatus` and typechecking `apps/api` is clean, `rebind-request.service.ts`
 * and the two `as const` spreads in `journey.ts` / `events-for-pallet.ts`
 * included. Kept because it is the truthful type and costs one mapped type,
 * and pinned by `order-status.test.ts` so it stays a narrowing rather than
 * quietly becoming decoration.
 */
export type OpenOrderEventStatus = {
  [S in OrderStatus]: (typeof ORDER_EVENT_OPENNESS)[S] extends 'open'
    ? S
    : never
}[OrderStatus]

/**
 * Is this trip still open — the one expression of the question, and the guard
 * `rebind-request.service.ts` narrows the swept set with.
 */
export function isOpenOrderEventStatus(
  status: OrderStatus,
): status is OpenOrderEventStatus {
  return ORDER_EVENT_OPENNESS[status] === 'open'
}

/**
 * Every open-trip predicate (booked-trip guards, retrieval reconciles, stack
 * floors, the site-access trip arm) filters on this one set; never hand-type
 * the triple at a call site.
 *
 * Derived rather than listed, so it cannot disagree with the record above. The
 * ORDER is `ORDER_STATUSES`' own, which no consumer depends on — every one of
 * them passes it to `inArray` or `.some`.
 */
export const OPEN_ORDER_EVENT_STATUSES: readonly OpenOrderEventStatus[] =
  ORDER_STATUSES.filter(isOpenOrderEventStatus)
